import React, { useState } from 'react';
import { MdClose, MdDownload, MdSend, MdCode, MdDescription, MdSchema } from 'react-icons/md';

const SubmissionModal = ({
  isOpen,
  onClose,
  submissionType = 'erd', // 'erd' | 'document' | 'code'
  // ERD-specific props
  erdNotation,
  erdData,
  svgContent,
  // Document-specific props
  documentContent,
  documentTitle,
  // Code-specific props
  codeContent,
  fileName,
  // Common props
  assignmentName,
  username,
  onSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleDownloadSVG = () => {
    if (!svgContent) return;

    let svgData = svgContent;

    if (svgContent.includes('data:image/svg+xml;base64,')) {
      const base64Match = svgContent.match(/data:image\/svg\+xml;base64,([^"]*)/);
      if (base64Match) {
        try {
          svgData = atob(base64Match[1]);
        } catch (e) {
          console.error('Error decoding base64 SVG:', e);
          return;
        }
      }
    }

    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `erd-diagram-${Date.now()}.svg`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadNotation = () => {
    try {
      const blob = new Blob([erdNotation], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `erd-notation-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading notation:', error);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const submissionData = submissionType === 'erd'
        ? { erdNotation, erdData, svgContent }
        : submissionType === 'document'
        ? { documentContent, documentTitle }
        : { codeContent, fileName };

      const success = await onSubmit(submissionData);

      if (success) {
        onClose();
      } else {
        throw new Error('Submission failed - no success response');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'Unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError(null);
    onClose();
  };

  const hasContent = submissionType === 'erd' ? !!erdNotation
    : submissionType === 'document' ? !!documentContent
    : !!codeContent;

  const typeLabel = submissionType === 'erd' ? 'ERD' : submissionType === 'document' ? 'Document' : 'Code';
  const TypeIcon = submissionType === 'erd' ? MdSchema : submissionType === 'document' ? MdDescription : MdCode;

  if (!isOpen) return null;

  const renderERDPreview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-800">ERD Notation</h3>
          <button
            onClick={handleDownloadNotation}
            className="flex items-center gap-2 text-blue-500 hover:text-blue-700 text-sm transition-colors"
          >
            <MdDownload className="w-4 h-4" />
            Download
          </button>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-80 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm font-mono text-gray-700">
            {erdNotation || 'No notation available'}
          </pre>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-800">ERD Diagram</h3>
          {svgContent && (
            <button
              onClick={handleDownloadSVG}
              className="flex items-center gap-2 text-blue-500 hover:text-blue-700 text-sm transition-colors"
            >
              <MdDownload className="w-4 h-4" />
              Download SVG
            </button>
          )}
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-80 overflow-auto">
          {svgContent ? (
            <div
              dangerouslySetInnerHTML={{ __html: svgContent }}
              className="w-full flex justify-center"
            />
          ) : (
            <p className="text-gray-500 text-center py-8">
              No diagram available. Please generate an ERD first.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderDocumentPreview = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-800">
        {documentTitle || 'Untitled Document'}
      </h3>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 max-h-96 overflow-y-auto">
        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
          {documentContent || 'No content available'}
        </div>
      </div>
    </div>
  );

  const renderCodePreview = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-800">
        {fileName || 'Code File'}
      </h3>
      <div className="bg-gray-900 border border-gray-700 rounded-md p-4 max-h-96 overflow-y-auto">
        <pre className="text-sm font-mono text-gray-100 whitespace-pre-wrap">
          {codeContent || 'No code available'}
        </pre>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <TypeIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">
              Submit {typeLabel} Assignment
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MdClose className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6">
            {/* Type-specific preview */}
            {submissionType === 'erd' && renderERDPreview()}
            {submissionType === 'document' && renderDocumentPreview()}
            {submissionType === 'code' && renderCodePreview()}

            {/* Submission Info */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-medium text-blue-800 mb-2">Submission Details</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p><strong>Assignment:</strong> {assignmentName || 'N/A'}</p>
                <p><strong>Student:</strong> {username}</p>
                <p><strong>Submission Date:</strong> {new Date().toLocaleString()}</p>
                <p><strong>Type:</strong> {typeLabel}</p>
              </div>
            </div>

            {/* Error Display */}
            {submitError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                <h4 className="font-medium text-red-800 mb-2">Submission Error</h4>
                <p className="text-sm text-red-700">{submitError}</p>
                <p className="text-sm text-red-600 mt-2">Please try again or contact your instructor if the problem persists.</p>
              </div>
            )}

            {/* Important Notice */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-medium text-blue-800 mb-2">Submission Information</h4>
              <div className="text-sm text-blue-700 space-y-2">
                <p>You can submit multiple times for this assignment</p>
                <p>Each submission will be saved with a version number</p>
                <p>Your instructor will grade your most recent submission</p>
                <p>You will receive a notification when your work is graded</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!hasContent || isSubmitting}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded-md transition-colors duration-200 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <MdSend className="w-4 h-4" />
                Submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionModal;
